<template>
  <div>
    <el-header>
      <!-- Temporary message for retailer views release -->
      <!-- TODO: Remove -->
      <transition
        name="slide-fade">
        <div
          v-if="hasPermission('role:retailer') && showRetailerViewsMessage"
          class="top-navigator__announcement-banner">
          👋 <strong>Hey, {{ firstName }}!</strong>
          What do you think about the new platform redesign?
          <span
            class="top-navigator__announcement-banner-button"
            @click="openRetailerSurvey()">
            Tell us what you think</span>
          <span
            class="top-navigator__announcement-banner-link"
            @click="hideRetailerMessage()">
            Hide
          </span>
        </div>
      </transition>

      <el-autocomplete
        v-if="hasPermission('feature:jump_to_page')"
        ref="jump"
        :fetch-suggestions="querySearch"
        :placeholder="jumpPlaceholder"
        v-model="jumpInput"
        :highlight-first-item="true"
        class="inline-input"
        @select="handleSelect">
        <template slot-scope="{ item }">
          &nbsp;&nbsp;{{ item.value }}
          <span
            v-if="!item.parameter && item.link"
            class="jump-menu__path">
            {{ item.link }}
          </span>
          <span
            v-else-if="item.parameter"
            class="jump-menu__path">
            id:{{ item.parameter }}
          </span>
        </template>
      </el-autocomplete>
      <el-dropdown style="float:right;">
        <div class="top-navigation-group clickable bordered">
          <img
            :src="avatarSrc"
            class="avatar top-navigation-item">
          <span class="top-navigation-item">{{ userName }}</span>
          <span class="top-navigation-item">
            <!-- eslint-disable-next-line vue/html-self-closing -->
            <i class="el-icon-arrow-down"></i>
          </span>
        </div>
        <el-dropdown-menu
          slot="dropdown"
          class="top-navigation-dropdown">
          <div
            class="top-navigation-link"
            @click="$router.replace({ name: 'UserDetails', params: { id: user.id } })">
            My Account
          </div>
          <div
            v-if="hasPermission('feature:switch_user') && !switched"
            class="top-navigation-link"
            @click="openUserSwitcherDialog">
            Switch User
          </div>
          <div
            v-if="!switched"
            class="top-navigation-link danger"
            @click="logout">
            Sign Out
            <icon
              style="float: right;"
              scale="0.75"
              name="arrow-right"/>
          </div>
          <div
            class="top-navigation-help">
            <el-tooltip
              content="Need help? Click to send us an email"
              placement="left">
              <a
                :href="mailToLink"
                class="top-navigation-help-link">
                <span class="top-navigation-help-text">support@grocerytv.com</span>
              </a>
            </el-tooltip>
          </div>
        </el-dropdown-menu>
      </el-dropdown>
    </el-header>
    <!-- User Switcher -->
    <el-dialog
      :visible.sync="userSwitcher.dialogVisible"
      :show-close="false"
      title="Switch Users"
      width="350px">
      <el-alert
        v-if="userSwitcherShowNoRoles"
        title="You may experience problems with the platform if you switch to a user with no roles."
        type="warning"
        show-icon/>
      <el-form>
        <el-form-item
          class="switcher__roles-toggle"
          label="Show users with no roles">
          <el-switch
            v-model="userSwitcherShowNoRoles"/>
        </el-form-item>
      </el-form>
      <el-select
        v-loading="userSwitcherUsers.length === 0"
        v-model="userSwitcher.userSelection"
        style="width: 100%;"
        filterable
        placeholder="Select a user">
        <el-option
          v-for="switchUser in switchableUsers"
          :disabled="switchUser.id === user.id"
          :key="switchUser.id"
          :label="`${switchUser.name} (${switchUser.roles.map(r => r.name).join(', ')})`"
          :value="switchUser.id">
          {{ switchUser.name }}
          <span class="switcher__roles-list">
            <span
              v-for="role in switchUser.roles"
              :key="role.id"
              class="switcher__role-tag">
              {{ role.name }}
            </span>
          </span>
        </el-option>
      </el-select>
      <span
        slot="footer"
        class="dialog-footer">
        <el-button
          @click="setUserSwitcherDialogState(false)">
          Cancel
        </el-button>
        <el-button
          :disabled="!userSwitcher.userSelection"
          type="primary"
          @click="switchUser">
          Switch
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>

// eslint-disable-next-line import/extensions
import Fuse from 'fuse.js/dist/fuse.min.js'
import { mapActions, mapGetters } from 'vuex'
import 'vue-awesome/icons/arrow-right'

export default {
  name: 'TopNavigator',
  data() {
    const platform = window.navigator.platform
    let jumpPlaceholder
    if (platform === 'MacIntel') {
      jumpPlaceholder = 'Jump to page (⌘ + P)'
    } else {
      jumpPlaceholder = 'Jump to page (Ctrl + P)'
    }
    const routeLinks = []
    const parameterizedLinks = []
    this.$router.options.routes.forEach((r) => {
      if (!r.meta || !r.meta.pageName) {
        return
      }
      if (!r.props) {
        routeLinks.push({
          value: r.meta.pageName,
          link: r.path,
        })
      } else if (r.props === true) {
        parameterizedLinks.push({
          value: r.meta.pageName,
          name: r.name,
          link: r.path,
        })
      }
    })
    routeLinks.push({ value: 'Switch User', action: this.openUserSwitcherDialog })
    return {
      jumpInput: '',
      jumpLinks: routeLinks,
      parameterizedLinks,
      jumpPlaceholder,
      showRetailerViewsMessage: localStorage.getItem('option:hide_retailer_views_message') === null
      // Disable until Sunday, April 7, 2019 7:00:00 PM GMT-05:00
      // Disable again after Saturday, April 20, 2019 7:00:00 PM GMT-05:00
      && Date.now() > 1554681600000 && Date.now() && Date.now() < 1555804800000,
      userSwitcherShowNoRoles: false,
    }
  },
  computed: {
    ...mapGetters([
      'userSwitcher',
      'userSwitcherUsers',
    ]),
    ...mapGetters('Auth', {
      switched: 'switched',
      user: 'user',
    }),
    mailToLink() {
      return `mailto:support@grocerytv.com?subject=Platform Help&body=
Please leave in the following information to help us resolve your issue:%0D%0A
------------------------------------------------%0D%0A
User email: ${this.user.email}%0D%0A
Current page: ${this.$route.path} (${this.$route.name})%0D%0A
------------------------------------------------%0D%0A
%0D%0A
Your message here:%0D%0A
%0D%0A
`
    },
    firstName() {
      const names = this.userName.split(' ')
      switch (names.length) {
        case 1:
          return names[0]
        case 2:
          return names[0]
        case 3:
          return `${names[0]} ${names[1]}`
        default:
          return this.userName
      }
    },
    userName() {
      return this.user.name
    },
    avatarSrc() {
      // Fetch avatar given user's name
      let tokens
      let fg = '657C95'
      let bg = 'FFFFFF'
      // Only send the user's initials to the 3rd party server
      if (!this.userName) {
        tokens = ['P', 'S']
      } else {
        tokens = this.userName.split(' ')
        if (tokens.length < 2) {
          tokens = ['P', 'S']
        }
      }
      if (this.$store.state.DarkMode.enabled) {
        bg = '000000'
        fg = 'eeeeee'
      }
      return `https://ui-avatars.com/api/?name=${tokens[0].charAt(0)}%20${tokens[1].charAt(0)}&rounded=true&color=${fg}&background=${bg}`
    },
    switchableUsers() {
      if (this.userSwitcherShowNoRoles) {
        return this.userSwitcherUsers
      }
      return this.userSwitcherUsers.filter((u) => {
        if (u.roles.length === 0) return false
        // Special case: user only receives emails
        if (u.roles.length === 1 && u.roles[0].name === 'Email Recipient') return false
        return true
      })
    },
  },
  created() {
    const vm = this
    /* eslint-disable-next-line prefer-arrow-callback */
    document.addEventListener('keydown', function handler(event) {
      if (!vm.$refs.jump) {
        return
      }
      if ((event.metaKey && event.keyCode === 80)
          || (event.ctrlKey && event.keyCode === 80)) {
        event.preventDefault()
        vm.jumpInput = ''
        vm.$refs.jump.focus()
      } else if (event.keyCode === 27) {
        vm.jumpInput = ''
      }
    })
  },
  methods: {
    ...mapActions([
      'setUserSwitcherDialogState',
    ]),
    createFilter(queryString) {
      /* eslint-disable-next-line arrow-body-style */
      return (link) => {
        return (link.value.toLowerCase().indexOf(queryString.toLowerCase()) === 0)
      }
    },
    hideRetailerMessage() {
      localStorage.setItem('option:hide_retailer_views_message', true)
      this.showRetailerViewsMessage = false
    },
    handleSelect(item) {
      this.jumpInput = ''
      if (item.link) {
        this.$router.push(item.link)
      } else if (item.action) {
        item.action()
      }
    },
    logout() {
      this.clearFilterCache()
      this.$store.dispatch('Auth/logout', {
        $router: this.$router,
      })
    },
    clearFilterCache() {
      localStorage.removeItem('CALL_QUEUE_SEARCH_FILTER')
      localStorage.removeItem('CALL_QUEUE_OWNER_FILTER')
      localStorage.removeItem('CALL_QUEUE_STATUS_FILTER')
      localStorage.removeItem('CALL_QUEUE_TYPE_FILTER')
      localStorage.removeItem('CALL_QUEUE_DATE_FILTER')
    },
    openUserSwitcherDialog() {
      this.$store.dispatch('setUserSwitcherDialogState', true)
    },
    openRetailerSurvey() {
      window.open('https://getpopspots.typeform.com/to/KFzt7H', '_blank')
    },
    querySearch(queryString, cb) {
      const links = this.jumpLinks
      if (!queryString || queryString === '') {
        return cb(links)
      }
      if (!isNaN(queryString)) {
        // eslint-disable-next-line arrow-body-style
        return cb(this.parameterizedLinks.map((l) => {
          return {
            value: l.value,
            parameter: queryString,
            action: () => {
              this.$router.push({
                name: l.name,
                params: {
                  id: queryString,
                },
              })
            },
          }
        }))
      }
      const fuse = new Fuse(links, {
        shouldSort: true,
        tokenize: true,
        matchAllTokens: false,
        threshold: 0.5,
        location: 0,
        distance: 60,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
          'value',
          'link',
        ],
      })
      const results = fuse.search(queryString)
      return cb(results)
    },
    switchUser() {
      if (!this.userSwitcher.userSelection) {
        return // Do nothing
      }

      this.$store.dispatch('switchUser', {
        $router: this.$router,
      })
      this.setUserSwitcherDialogState(false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/_color.scss';
@import '@/assets/styles/base/_dark.scss';

.slide-fade-enter-active {
transition: all .3s ease;
}
.slide-fade-leave-active {
  transition: all .3s cubic-bezier(0.8, 0.5, 0.7, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(7px);
  opacity: 0;
}

.switcher__roles-toggle {
  margin: 0;
}
.switcher__role-tag {
  background-color: #ebeef9;
  border-radius: 2px;
  color: #5f6972;
  font-size: 8pt;
  line-height: 10pt;
  margin-left: 8px;
  padding: 1px 4px;
}
.top-navigator__announcement-banner {
  display: inline-block;
  width: 70%;
  padding-left: 10px;
  font-size: 10pt;
}
.top-navigator__announcement-banner-button {
  cursor: pointer;
  padding: 2px 5px;
  background-color: $color-cobalt;
  border-radius: 2px;
  color: #ffffff;
  margin-left: 10px;
}
.top-navigator__announcement-banner-link {
  text-decoration: underline;
  margin-left: 10px;
  cursor: pointer;
  font-size: 9pt;
}
.el-header {
  background-color: #ffffff;
  border-bottom: 1px solid #e3e8ee;
  font-size: 12px;
  height: 60px;
  left: 0;
  line-height: 60px;
  padding-left: 220px; /* Padding for sidebar */
  position: fixed;
  text-align: left;
  top: 0;
  width: 100%;
  z-index: 150; /* TODO: Fix issue with loader */
  body[data-theme='dark'] & {
    background-color: $dark-mode-component-background-color;
    border-bottom: none;
  }
}
.el-header .el-autocomplete {
  width: 30%;
}
.top-navigation-dropdown {
  margin-top: 5px;
  width: 250px;
  font-size: 11pt;
  color: $color__font--body;
  body[data-theme='dark'] & {
    color: $dark-mode-text-color;
  }
}
.top-navigation-link {
  cursor: pointer;
  padding: 5px 10px;
}
.top-navigation-help {
  margin-top: 5px;
  border-top: 1px solid #e3e8ee;
  padding-top: 10px;
  text-align: center;
}
.top-navigation-help-text {
  border-radius: 2px;
  border: 1px solid #b6c3d2;
  font-size: 9pt;
  padding: 2px 15px;
}
a.top-navigation-help-link {
  color: #5f6972;
  text-decoration: none;
}
.top-navigation-help-text:hover {
  background-color: #fafbfc;
}
.top-navigation-link.danger {
  font-weight: 500;
}
.top-navigation-link:hover {
  background-color: #fafbfc;
}
.avatar {
  border-radius: 50%;
  border: 1px solid #e3e8ee;
  display: inline-block;
  height: 36px;
  vertical-align: middle;
  body[data-theme='dark'] & {
    border: none;
  }
}
.top-navigation-item {
  margin: 0 10px;
  vertical-align: middle;
}
.top-navigation-group .fa-icon {
  color: #898b90;
}
.top-navigation-group {
  box-sizing: border-box;
  display: inline-block;
  height: 60px;
  padding-left: 15px;
  padding-right: 15px;
  margin: 0;
}
.top-navigation-group.bordered {
  border-left: 1px solid #e3e8ee;
  border-right: 1px solid #e3e8ee;
  body[data-theme='dark'] & {
    border-left: 1px solid $dark-mode-background-color;
    border-right: 1px solid $dark-mode-background-color;
  }
}
.top-navigation-group.clickable {
  cursor: pointer;
}
.top-navigation-group:hover {
  background-color: $color-hover;
  border-bottom: 1px solid $color__border;
  body[data-theme='dark'] & {
    background-color: $dark-mode-hover-background-color;
    border-bottom: none;
  }
}
.jump-menu__path {
  margin-left: 5px;
  background-color: #ebeef9;
  border-radius: 2px;
  color: #5f6972;
  font-size: 8pt;
  line-height: 10pt;
  vertical-align: middle;
  padding: 1px 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/navigation/TopNavigator.vue